import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { PersonSearch } from '@/components/persons/PersonSearch';
import { guestWithPersonSchema, type GuestWithPersonFormData } from '@/lib/schemas/guest.schema';
import { toast } from 'sonner';
import { Loader2, UserPlus } from 'lucide-react';
import { Separator } from '@/components/ui/separator';

interface GuestFormWithPersonProps {
  trigger?: React.ReactNode;
  defaultReservationId?: string;
  onSuccess?: () => void;
}

export function GuestFormWithPerson({ 
  trigger, 
  defaultReservationId,
  onSuccess 
}: GuestFormWithPersonProps) {
  const [open, setOpen] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<any>(null);
  const queryClient = useQueryClient();

  const form = useForm<GuestWithPersonFormData>({
    resolver: zodResolver(guestWithPersonSchema),
    defaultValues: {
      reservationId: defaultReservationId || '',
      personId: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      arrivalTime: '',
      specialRequests: '',
    },
  });

  // Fetch reservations without guests
  const { data: reservations, isLoading: loadingReservations } = useQuery({
    queryKey: ['reservations-without-full-guests'],
    queryFn: async () => {
      const response = await api.get('/reservations');
      // Filter reservations that might need more guests
      return response.data;
    },
  });

  // Create guest mutation
  const createGuest = useMutation({
    mutationFn: async ({ endpoint, payload }: { endpoint: string; payload: any }) => {
      const response = await api.post(endpoint, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['viatgers'] });
      queryClient.invalidateQueries({ queryKey: ['reservation-guests'] });
      queryClient.invalidateQueries({ queryKey: ['mossos-viatgers'] });
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      toast.success('Huésped añadido correctamente');
      setOpen(false);
      form.reset();
      setSelectedPerson(null);
      onSuccess?.();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'No se pudo añadir el huésped');
    },
  });

  const handlePersonSelect = (person: any) => {
    setSelectedPerson(person);
    form.setValue('personId', person.id);
    // Pre-fill form with person data
    form.setValue('firstName', person.firstName || '');
    form.setValue('lastName', person.lastName || '');
    form.setValue('email', person.email || '');
    form.setValue('phone', person.phone || '');
    form.setValue('documentType', person.documentType || undefined);
    form.setValue('documentNumber', person.documentNumber || '');
  };

  // Mapeig UI → codis de document Mossos (D/N/P/O)
  const mapDocumentType = (type?: string) => {
    const mapping: Record<string, string> = { DNI: 'D', NIE: 'N', PASSPORT: 'P', OTHER: 'O' };
    return type ? mapping[type] || 'O' : 'O';
  };

  const onSubmit = async (data: GuestWithPersonFormData) => {
    try {
      // If person exists, link it to the reservation (copies all person data)
      if (selectedPerson) {
        await createGuest.mutateAsync({
          endpoint: '/viatgers/from-person',
          payload: {
            personId: selectedPerson.id,
            reservationId: data.reservationId,
          },
        });
      } else {
        // Create new guest (CreateGuestDto català)
        await createGuest.mutateAsync({
          endpoint: '/viatgers',
          payload: {
            reserva_id: data.reservationId,
            nom: data.firstName,
            cognoms: data.lastName,
            email: data.email || undefined,
            telefon: data.phone || undefined,
            tipus_document: mapDocumentType(data.documentType),
            num_document: data.documentNumber,
            nacionalitat: 'ESP',
            data_naixement: data.dateOfBirth || undefined,
            adreca: data.address || undefined,
            ciutat: data.city || undefined,
            codi_postal: data.postalCode || undefined,
            pais: data.country || undefined,
            peticions_especials: data.specialRequests || undefined,
            consentiment_rgpd: true,
          },
        });
      }
    } catch (error) {
      // Error handled by mutation
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button>
            <UserPlus className="mr-2 h-4 w-4" />
            Añadir Huésped
          </Button>
        )}
      </DialogTrigger>
      <DialogContent size="xl" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Añadir Huésped a Reserva</DialogTitle>
          <DialogDescription>
            Busca un huésped existente o crea uno nuevo
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Seleccionar Reserva */}
            <FormField
              control={form.control}
              name="reservationId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reserva</FormLabel>
                  <Select 
                    onValueChange={field.onChange} 
                    defaultValue={field.value}
                    disabled={!!defaultReservationId}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona una reserva" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {loadingReservations ? (
                        <SelectItem value="loading" disabled>
                          Cargando...
                        </SelectItem>
                      ) : reservations && reservations.length > 0 ? (
                        reservations.map((r: any) => (
                          <SelectItem key={r.id} value={r.id}>
                            Beds24 #{r.beds24Id} - {r.checkIn} → {r.checkOut}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="none" disabled>
                          No hay reservas disponibles
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Separator />

            {/* Buscar Person existente */}
            <div className="space-y-2">
              <FormLabel>Buscar Huésped Existente</FormLabel>
              <PersonSearch
                onSelect={handlePersonSelect}
                placeholder="Buscar por nombre, email o documento..."
              />
              {selectedPerson && (
                <div className="text-sm text-muted-foreground mt-2 p-2 bg-muted rounded-md">
                  ✓ Seleccionado: <strong>{selectedPerson.firstName} {selectedPerson.lastName}</strong>
                  {selectedPerson.email && ` (${selectedPerson.email})`}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="ml-2"
                    onClick={() => {
                      setSelectedPerson(null);
                      form.setValue('personId', '');
                    }}
                  >
                    Cambiar
                  </Button>
                </div>
              )}
            </div>

            {!selectedPerson && (
              <>
                <Separator />
                <div className="space-y-4">
                  <FormLabel className="text-base">O crear nuevo huésped</FormLabel>

                  {/* Nombre */}
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Nombre</FormLabel>
                          <FormControl>
                            <Input placeholder="Juan" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Apellidos</FormLabel>
                          <FormControl>
                            <Input placeholder="García" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Email y Teléfono */}
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input type="email" placeholder="juan@example.com" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Teléfono</FormLabel>
                          <FormControl>
                            <Input placeholder="+34 600 123 456" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Documento */}
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="documentType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Tipo Documento</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Selecciona tipo" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="DNI">DNI</SelectItem>
                              <SelectItem value="NIE">NIE</SelectItem>
                              <SelectItem value="PASSPORT">Pasaporte</SelectItem>
                              <SelectItem value="OTHER">Otro</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="documentNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Número Documento</FormLabel>
                          <FormControl>
                            <Input placeholder="12345678X" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Fecha de nacimiento (requerida por el registro de Mossos) */}
                  <FormField
                    control={form.control}
                    name="dateOfBirth"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Fecha de nacimiento</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </>
            )}

            <Separator />

            {/* Guest-specific fields */}
            <div className="space-y-4">
              <FormLabel className="text-base">Información de la estancia</FormLabel>

              <FormField
                control={form.control}
                name="arrivalTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Hora de llegada (opcional)</FormLabel>
                    <FormControl>
                      <Input type="time" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="specialRequests"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Peticiones especiales (opcional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Cama supletoria, cuna, alergias..."
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setOpen(false);
                  form.reset();
                  setSelectedPerson(null);
                }}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={createGuest.isPending}>
                {createGuest.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Añadir Huésped
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
